import {
  getFinancialPerformance,
  getFinancialPerformanceProcurement,
  Site,
  TimedTariffAggregateEnergyFlow,
} from "@inrange/building-manager-api-client/models-site";
import {
  ChartData,
  OperationalChartDataPoint,
  OperationalSeriesData,
} from "@inrange/theme-components/charts";
import { toNearestTen } from "@inrange/theme-components/formatting";

const months = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const hasAnyTariffArbitrage = (site: Site): boolean => {
  return site.energyFinancialsMonthly.some(
    (month) => (month.energy.forecast?.batteryGridDischarge ?? 0) > 0
  );
};

const hasAnyNetworkImport = (site: Site): boolean => {
  return site.energyFinancialsMonthly.some(
    (month) => (month.energy.forecast?.networkImport ?? 0) > 0
  );
};

const hasAnyExport = (site: Site): boolean => {
  return site.energyFinancialsMonthly.some(
    (month) =>
      (month.energy.forecast?.networkExport ?? 0) +
        (month.energy.forecast?.exported ?? 0) >
      0
  );
};

export const genSiteGenerationConsumptionChartData = (
  site: Site
): ChartData[] => {
  const chartData: ChartData[] = [];
  let hour = 0;
  let minute = 0;

  for (let ix = 0; ix < site.energyFlowSummerDay.generation.length; ix++) {
    const hourText = hour.toString().padStart(2, "0");
    const minuteText = minute.toString().padStart(2, "0");

    chartData.push({
      index: `${hourText}:${minuteText}`,
      Generation: Math.round(site.energyFlowSummerDay.generation[ix]),
      Demand: Math.round(site.energyFlowSummerDay.demand[ix]),
    });

    if (minute === 30) {
      minute = 0;
      hour += 1;
    } else {
      minute += 30;
    }
  }
  return chartData;
};

export const getCostOfEnergyProcuredChartData = (
  site: Site,
  isOwnerOccupier: boolean,
  investmentModel: string
): OperationalChartDataPoint[] => {
  const chartData: OperationalChartDataPoint[] = [];

  for (const energyFinancialsMonth of site.energyFinancialsMonthly) {
    const forecast: OperationalSeriesData[] = [];
    const actual: OperationalSeriesData[] = [];

    const forecastProcurement = getFinancialPerformanceProcurement(
      energyFinancialsMonth.financial.forecast,
      isOwnerOccupier,
      investmentModel
    );
    const actualProcurement = getFinancialPerformanceProcurement(
      energyFinancialsMonth.financial.actual,
      isOwnerOccupier,
      investmentModel
    );

    if (site.totalBatterySummary) {
      forecast.push({
        key: "Forecast on-site direct from PV and battery",
        value: Math.round(
          (forecastProcurement?.costOnsitePV ?? 0) +
            (forecastProcurement?.costOnsiteBattery ?? 0)
        ),
      });
      actual.push({
        key: "On-site direct from PV and battery",
        value:
          (actualProcurement?.costOnsitePV ?? 0) +
          (actualProcurement?.costOnsiteBattery ?? 0),
      });
    } else if (site.totalInstalledCapacity > 0) {
      forecast.push({
        key: "Forecast on-site direct from PV",
        value: Math.round(forecastProcurement?.costOnsitePV ?? 0),
      });
      actual.push({
        key: "On-site direct from PV",
        value: actualProcurement?.costOnsitePV ?? 0,
      });
    }

    if (hasAnyNetworkImport(site)) {
      forecast.push({
        key: "Forecast from InRange network",
        value: Math.round(forecastProcurement?.costNetworkImport ?? 0),
      });
      actual.push({
        key: "From InRange network",
        value: actualProcurement?.costNetworkImport ?? 0,
      });
    }

    const hasActualData = !!energyFinancialsMonth.actualMetadata;

    chartData.push({
      index: months[energyFinancialsMonth.month - 1],
      forecast,
      actual: hasActualData ? actual : undefined,
      meta_first_day: energyFinancialsMonth.actualMetadata?.first_day,
      meta_last_day: energyFinancialsMonth.actualMetadata?.last_day,
    });
  }

  return chartData;
};

export const getProposalCostOfEnergyProcuredChartData = (
  site: Site,
  isOwnerOccupier: boolean,
  isOwnerOccupierLicense: boolean,
  investmentModel: string
): ChartData[] => {
  const chartData: ChartData[] = [];
  for (const energyFinancialsMonth of site.energyFinancialsMonthly) {
    const monthData: ChartData = {
      index: months[energyFinancialsMonth.month - 1],
    };
    const procurement = getFinancialPerformanceProcurement(
      energyFinancialsMonth.financial.forecast,
      isOwnerOccupier,
      investmentModel
    );
    if (site.totalInstalledCapacity > 0) {
      if (!isOwnerOccupierLicense) {
        monthData["On-site direct from PV"] = toNearestTen(
          procurement?.costOnsitePV ?? 0
        );
        if (site.energyFlowAnnual.behindMeterBattery > 0) {
          monthData["On-site battery discharge from PV"] = toNearestTen(
            procurement?.costOnsiteBattery ?? 0
          );
        }
      }
    }
    if (site.energyFlowAnnual.batteryGridDischarge > 0) {
      monthData["On-site peak-shifted by battery"] = toNearestTen(
        procurement?.costOnsiteTariffArbitrage ?? 0
      );
    }
    monthData["From InRange network"] = toNearestTen(
      procurement?.costNetworkImport ?? 0
    );
    chartData.push(monthData);
  }

  return chartData;
};

export const getAmountOfEnergyProcuredChartData = (
  site: Site
): OperationalChartDataPoint[] => {
  const chartData: OperationalChartDataPoint[] = [];

  for (const energyFinancialsMonth of site.energyFinancialsMonthly) {
    const forecast: OperationalSeriesData[] = [];
    const actual: OperationalSeriesData[] = [];

    if (site.totalInstalledCapacity > 0) {
      // On-site direct from PV
      forecast.push({
        key: "Forecast on-site direct from PV",
        value: Math.round(
          energyFinancialsMonth.energy.forecast?.behindMeterPV ?? 0
        ),
      });
      actual.push({
        key: "On-site direct from PV",
        value: energyFinancialsMonth.energy.actual?.behindMeterPV ?? 0,
      });
    }

    // Battery discharge from PV
    if (site.totalBatterySummary) {
      forecast.push({
        key: "Forecast on-site battery discharge from PV",
        value: Math.round(
          energyFinancialsMonth.energy.forecast?.behindMeterBattery ?? 0
        ),
      });
      actual.push({
        key: "On-site battery discharge from PV",
        value: energyFinancialsMonth.energy.actual?.behindMeterBattery ?? 0,
      });
    }

    // Tariff arbitrage
    if (hasAnyTariffArbitrage(site)) {
      forecast.push({
        key: "Forecast on-site peak-shifted by battery",
        value: Math.round(
          energyFinancialsMonth.energy.forecast?.behindMeterBatteryGrid ?? 0
        ),
      });
      actual.push({
        key: "On-site peak-shifted by battery",
        value: energyFinancialsMonth.energy.actual?.behindMeterBatteryGrid ?? 0,
      });
    }

    // Network import
    if (hasAnyNetworkImport(site)) {
      forecast.push({
        key: "Forecast from InRange network",
        value: Math.round(
          energyFinancialsMonth.energy.forecast?.networkImport ?? 0
        ),
      });
      actual.push({
        key: "From InRange network",
        value: energyFinancialsMonth.energy.actual?.networkImport ?? 0,
      });
    }

    const hasActualData = !!energyFinancialsMonth.actualMetadata;

    chartData.push({
      index: months[energyFinancialsMonth.month - 1],
      forecast,
      actual: hasActualData ? actual : undefined,
      meta_first_day: energyFinancialsMonth.actualMetadata?.first_day,
      meta_last_day: energyFinancialsMonth.actualMetadata?.last_day,
    });
  }

  return chartData;
};

export const getProposalRevenueChartData = (
  site: Site,
  isOwnerOccupier: boolean,
  isOwnerOccupierLicense: boolean,
  investmentModel: string
): ChartData[] => {
  const chartData: ChartData[] = [];

  for (const energyFinancialsMonth of site.energyFinancialsMonthly) {
    const monthData: ChartData = {
      index: months[energyFinancialsMonth.month - 1],
    };
    if (!isOwnerOccupierLicense) {
      if (site.totalBatterySummary) {
        monthData["On-site direct from PV and battery"] = toNearestTen(
          (getFinancialPerformance(
            energyFinancialsMonth.financial.forecast,
            isOwnerOccupier,
            investmentModel
          )?.revenueOnsitePV ?? 0) +
            (getFinancialPerformance(
              energyFinancialsMonth.financial.forecast,
              isOwnerOccupier,
              investmentModel
            )?.revenueOnsiteBattery ?? 0)
        );
      } else {
        monthData["On-site direct from PV"] = toNearestTen(
          getFinancialPerformance(
            energyFinancialsMonth.financial.forecast,
            isOwnerOccupier,
            investmentModel
          )?.revenueOnsitePV ?? 0
        );
      }
    }
    if (hasAnyTariffArbitrage(site)) {
      monthData["On-site peak-shifted by battery"] = toNearestTen(
        getFinancialPerformance(
          energyFinancialsMonth.financial.forecast,
          isOwnerOccupier,
          investmentModel
        )?.revenueOnsiteTariffArbitrage ?? 0
      );
    }
    if (hasAnyExport(site)) {
      monthData["Exported to network"] = toNearestTen(
        getFinancialPerformance(
          energyFinancialsMonth.financial.forecast,
          isOwnerOccupier,
          investmentModel
        )?.revenueExport ?? 0
      );
    }

    chartData.push(monthData);
  }

  return chartData;
};

export const getOperationalRevenueChartData = (
  site: Site,
  isOwnerOccupier: boolean,
  isOwnerOccupierLicense: boolean,
  investmentModel: string
): OperationalChartDataPoint[] => {
  const chartData: OperationalChartDataPoint[] = [];

  for (const energyFinancialsMonth of site.energyFinancialsMonthly) {
    const forecast: OperationalSeriesData[] = [];
    const actual: OperationalSeriesData[] = [];

    const forecastPerformance = getFinancialPerformance(
      energyFinancialsMonth.financial.forecast,
      isOwnerOccupier,
      investmentModel
    );
    const actualPerformance = getFinancialPerformance(
      energyFinancialsMonth.financial.actual,
      isOwnerOccupier,
      investmentModel
    );

    if (!isOwnerOccupierLicense) {
      if (site.totalBatterySummary) {
        forecast.push({
          key: "Forecast on-site direct from PV and battery",
          value: Math.round(
            (forecastPerformance?.revenueOnsitePV ?? 0) +
              (forecastPerformance?.revenueOnsiteBattery ?? 0)
          ),
        });
        actual.push({
          key: "On-site direct from PV and battery",
          value:
            (actualPerformance?.revenueOnsitePV ?? 0) +
            (actualPerformance?.revenueOnsiteBattery ?? 0),
        });
      } else {
        forecast.push({
          key: "Forecast on-site direct from PV",
          value: Math.round(forecastPerformance?.revenueOnsitePV ?? 0),
        });
        actual.push({
          key: "On-site direct from PV",
          value: actualPerformance?.revenueOnsitePV ?? 0,
        });
      }

      if (hasAnyTariffArbitrage(site)) {
        forecast.push({
          key: "Forecast on-site peak-shifted by battery",
          value: Math.round(
            forecastPerformance?.revenueOnsiteTariffArbitrage ?? 0
          ),
        });
        actual.push({
          key: "On-site peak-shifted by battery",
          value: actualPerformance?.revenueOnsiteTariffArbitrage ?? 0,
        });
      }
    }

    if (hasAnyExport(site)) {
      forecast.push({
        key: "Forecast exported to network",
        value: Math.round(forecastPerformance?.revenueExport ?? 0),
      });
      actual.push({
        key: "Exported to network",
        value: actualPerformance?.revenueExport ?? 0,
      });
    }

    const hasActualData = !!energyFinancialsMonth.actualMetadata;

    chartData.push({
      index: months[energyFinancialsMonth.month - 1],
      forecast,
      actual: hasActualData ? actual : undefined,
      meta_first_day: energyFinancialsMonth.actualMetadata?.first_day,
      meta_last_day: energyFinancialsMonth.actualMetadata?.last_day,
    });
  }

  return chartData;
};

export const getProposalEnergySoldChartData = (site: Site): ChartData[] => {
  const chartData: ChartData[] = [];

  for (const energyFinancialsMonth of site.energyFinancialsMonthly) {
    const data: ChartData = {
      index: months[energyFinancialsMonth.month - 1],
    };
    if (site.totalInstalledCapacity > 0) {
      data["On-site direct from PV"] = toNearestTen(
        energyFinancialsMonth.energy.forecast?.behindMeterPV ?? 0
      );
      if (site.energyFlowAnnual.behindMeterBattery > 0) {
        data["On-site battery discharge from PV"] = toNearestTen(
          energyFinancialsMonth.energy.forecast?.behindMeterBattery ?? 0
        );
      }
      if (site.energyFlowAnnual.batteryGridDischarge > 0) {
        data["On-site peak-shifted by battery"] = toNearestTen(
          energyFinancialsMonth.energy.forecast?.behindMeterBatteryGrid ?? 0
        );
      }
    }
    data["Exported to network"] = toNearestTen(
      (energyFinancialsMonth.energy.forecast?.exported ?? 0) +
        (energyFinancialsMonth.energy.forecast?.networkExport ?? 0)
    );
    chartData.push(data);
  }

  return chartData;
};

export const getOperationalEnergySoldChartData = (
  site: Site,
  isOwnerOccupierLicense: boolean
): OperationalChartDataPoint[] => {
  const chartData: OperationalChartDataPoint[] = [];

  for (const energyFinancialsMonth of site.energyFinancialsMonthly) {
    const forecast: OperationalSeriesData[] = [];
    const actual: OperationalSeriesData[] = [];

    if (!isOwnerOccupierLicense) {
      forecast.push({
        key: "Forecast on-site direct from PV",
        value: Math.round(
          energyFinancialsMonth.energy.forecast?.behindMeterPV ?? 0
        ),
      });
      actual.push({
        key: "On-site direct from PV",
        value: energyFinancialsMonth.energy.actual?.behindMeterPV ?? 0,
      });

      if (site.totalBatterySummary) {
        forecast.push({
          key: "Forecast on-site battery discharge from PV",
          value: Math.round(
            energyFinancialsMonth.energy.forecast?.behindMeterBattery ?? 0
          ),
        });
        actual.push({
          key: "On-site battery discharge from PV",
          value: energyFinancialsMonth.energy.actual?.behindMeterBattery ?? 0,
        });
      }
    }

    if (hasAnyTariffArbitrage(site)) {
      forecast.push({
        key: "Forecast on-site peak-shifted by battery",
        value: Math.round(
          energyFinancialsMonth.energy.forecast?.behindMeterBatteryGrid ?? 0
        ),
      });
      actual.push({
        key: "On-site peak-shifted by battery",
        value: energyFinancialsMonth.energy.actual?.behindMeterBatteryGrid ?? 0,
      });
    }

    if (hasAnyExport(site)) {
      forecast.push({
        key: "Forecast exported to network",
        value: Math.round(
          (energyFinancialsMonth.energy.forecast?.networkExport ?? 0) +
            (energyFinancialsMonth.energy.forecast?.exported ?? 0)
        ),
      });
      actual.push({
        key: "Exported to network",
        value:
          (energyFinancialsMonth.energy.actual?.networkExport ?? 0) +
          (energyFinancialsMonth.energy.actual?.exported ?? 0),
      });
    }

    const hasActualData = !!energyFinancialsMonth.actualMetadata;

    chartData.push({
      index: months[energyFinancialsMonth.month - 1],
      forecast,
      actual: hasActualData ? actual : undefined,
      meta_first_day: energyFinancialsMonth.actualMetadata?.first_day,
      meta_last_day: energyFinancialsMonth.actualMetadata?.last_day,
    });
  }

  return chartData;
};

export const genEnergyProcuredChartData = (
  site: Site,
  period: string
): ChartData[] => {
  const isBatterySite =
    site.totalInstalledCapacity > 0 && !!site.totalBatterySummary;
  const isBatteryArbitrageSite = site.energyFlowAnnual.batteryGridCharge > 0;
  if (period === "year")
    return genEnergyProcuredChartDataYear(
      site,
      isBatterySite,
      isBatteryArbitrageSite
    );
  return genEnergyProcuredChartDataDay(
    site,
    isBatterySite,
    isBatteryArbitrageSite,
    period
  );
};

export const genEnergyProcuredChartDataDay = (
  site: Site,
  isBatterySite: boolean,
  isBatteryArbitrageSite: boolean,
  period: string
): ChartData[] => {
  const chartData: ChartData[] = [];

  let hour = 0;
  let minute = 0;

  for (const _ of site.energyFlowSummerDay.behindMeterPV) {
    const hourText = hour.toString().padStart(2, "0");
    const minuteText = minute.toString().padStart(2, "0");

    chartData.push({
      index: `${hourText}:${minuteText}`,
    });

    if (minute === 30) {
      minute = 0;
      hour += 1;
    } else {
      minute += 30;
    }
  }

  const energyFlowDay =
    period === "summer" ? site.energyFlowSummerDay : site.energyFlowWinterDay;
  for (let ix = 0; ix < energyFlowDay.behindMeterPV.length; ix++) {
    // For half hourly level data we round to nearest integer rather than nearest 10
    if (site.totalInstalledCapacity > 0) {
      chartData[ix]["On-site direct from PV"] = Math.round(
        energyFlowDay.behindMeterPV[ix]
      );
      if (isBatterySite) {
        chartData[ix]["On-site battery discharge from PV"] = Math.round(
          energyFlowDay.behindMeterBattery[ix]
        );
        if (isBatteryArbitrageSite) {
          chartData[ix]["On-site peak-shifted by battery"] = Math.round(
            energyFlowDay.behindMeterBatteryGrid[ix]
          );
        }
      }
    }
    chartData[ix]["From InRange network"] = Math.round(
      energyFlowDay.networkImport[ix]
    );
    chartData[ix]["From other suppliers"] = Math.round(
      energyFlowDay.gridImport[ix]
    );
  }

  return chartData;
};

export const genEnergyProcuredChartDataYear = (
  site: Site,
  isBatterySite: boolean,
  isBatteryArbitrageSite: boolean
): ChartData[] => {
  const result: ChartData[] = [];
  for (const energyFinancialsMonth of site.energyFinancialsMonthly) {
    const chartData: ChartData = {
      index: months[energyFinancialsMonth.month - 1],
    };
    if (site.totalInstalledCapacity > 0) {
      chartData["On-site direct from PV"] = toNearestTen(
        energyFinancialsMonth.energy.forecast?.behindMeter ?? 0
      );
      if (isBatterySite) {
        chartData["On-site battery discharge from PV"] = toNearestTen(
          energyFinancialsMonth.energy.forecast?.behindMeterBattery ?? 0
        );
        if (isBatteryArbitrageSite) {
          chartData["On-site peak-shifted by battery"] = toNearestTen(
            energyFinancialsMonth.energy.forecast?.behindMeterBatteryGrid ?? 0
          );
        }
      }
    }
    chartData["From InRange network"] = toNearestTen(
      energyFinancialsMonth.energy.forecast?.networkImport ?? 0
    );
    chartData["From other suppliers"] = toNearestTen(
      energyFinancialsMonth.energy.forecast?.gridImport ?? 0
    );
    result.push(chartData);
  }
  return result;
};

export const genPeakOffPeakChartDataYear = (site: Site): ChartData[] => {
  const result: ChartData[] = [];
  for (const energyFinancialsMonth of site.energyFinancialsMonthly) {
    const timedTariffsByTariffRate: Record<
      string,
      Record<string, TimedTariffAggregateEnergyFlow>
    > = energyFinancialsMonth.energy.forecast?.timedImportTariffs.reduce(
      (acc, tariff) => {
        if (!acc[tariff.tariffName]) {
          acc[tariff.tariffName] = {};
        }
        if (!acc[tariff.tariffName][tariff.rateName]) {
          acc[tariff.tariffName][tariff.rateName] = tariff;
        }
        return acc;
      },
      {}
    ) || {};
    const chartData: ChartData = {
      index: months[energyFinancialsMonth.month - 1],
    };
    if (site.totalInstalledCapacity > 0) {
      chartData["Peak hour energy supplied from on-site generation"] =
        toNearestTen(
          timedTariffsByTariffRate["Peak/Off-Peak"]?.["Peak"]?.behindMeter ?? 0
        );
    }
    if (site.energyFlowAnnual.behindMeterBatteryGrid > 0) {
      chartData[
        "Peak hour energy supplied by on-site peak-shifting by battery"
      ] = toNearestTen(
        timedTariffsByTariffRate["Peak/Off-Peak"]?.["Peak"]?.behindMeterGrid ??
          0
      );
    }
    chartData["Peak hour energy supplied from InRange Network"] = toNearestTen(
      timedTariffsByTariffRate["Peak/Off-Peak"]?.["Peak"]?.networkImport ?? 0
    );
    chartData["Peak hour energy not supplied from InRange"] = toNearestTen(
      timedTariffsByTariffRate["Peak/Off-Peak"]?.["Peak"]?.grid ?? 0
    );
    chartData["Off-Peak hour energy not supplied from InRange"] = toNearestTen(
      timedTariffsByTariffRate["Peak/Off-Peak"]?.["Off-Peak"]?.grid ?? 0
    );
    result.push(chartData);
  }
  return result;
};
